import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, FileDown, AlertTriangle } from "lucide-react";
import { getStatement, statementPdfUrl, zar } from "@/api";

export default function StatementView() {
  const { token } = useParams();
  const [st, setSt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getStatement(token)
      .then(setSt)
      .catch(() => setError("This statement link is invalid or has expired."));
  }, [token]);

  if (error) {
    return (
      <div data-testid="statement-error" className="min-h-screen flex items-center justify-center px-4 pt-20 text-center">
        <div>
          <AlertTriangle size={48} className="text-amber-400 mx-auto mb-6" />
          <h1 className="font-display text-3xl font-black text-white mb-3">Link not valid</h1>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!st) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="statement-loading">Loading statement…</div>;
  }

  const rows = [
    ["Ownership allocation / month", zar(st.allocation_pm)],
    ["Payments received", `${st.payments_made} of ${st.term}`],
    ["Ownership paid to date", zar(st.paid)],
    ["Ownership balance remaining", zar(st.remaining)],
    ["Agreed term", `${st.term} months`],
    ...(st.debit_day ? [["Debit-order day", `Day ${st.debit_day} of each month`]] : []),
  ];

  return (
    <div data-testid="statement-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-2xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">CompCar Motors</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-3">
            Your ownership statement.
          </h1>
          <p className="text-sm text-slate-400 mb-10">
            {st.full_name} · {st.vehicle} · Reference <strong className="text-[#F57C00]">{st.reference}</strong>
          </p>

          <div className="bg-[#122659] border border-sky-300/20 rounded-xl p-8 mb-8" data-testid="statement-progress-card">
            <div className="flex items-center gap-3 mb-5">
              <TrendingUp size={22} className="text-[#F57C00]" />
              <p className="font-display text-lg font-bold text-white">Ownership progress</p>
            </div>
            <div className="h-4 rounded-full bg-[#0B1F4E] border border-sky-300/20 overflow-hidden mb-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${st.progress}%` }}
                transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="h-full bg-[#F57C00] rounded-full"
                data-testid="statement-progress-bar"
              />
            </div>
            <p className="font-display text-3xl font-black text-[#F57C00]" data-testid="statement-progress-pct">{st.progress}%</p>
            <p className="text-xs text-slate-500 mt-1">of your ownership balance is paid</p>
          </div>

          <div className="bg-[#122659]/60 border border-sky-300/20 rounded-xl p-8 mb-8">
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between items-center text-sm border-b border-white/5 py-3 last:border-0">
                <span className="text-slate-400">{label}</span>
                <span className="font-semibold text-white">{value}</span>
              </div>
            ))}
          </div>

          <a
            href={statementPdfUrl(token)}
            data-testid="statement-download-btn"
            className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-7 py-4 rounded-md transition-colors duration-300"
          >
            <FileDown size={18} /> Download PDF
          </a>
          <p className="text-[11px] text-slate-500 mt-8 leading-relaxed">
            Every on-time payment builds your ownership balance. When it reaches
            zero, the vehicle is registered in your name. Questions? Call
            010 534 9926 or email admin@compcarmotors.co.za.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
